import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { HelpCircle, X } from "lucide-react";
import { getRole } from "../auth/session";
import { type Role } from "./types";
import { articleForPath } from "./content";
import { HelpArticleView } from "./HelpArticleView";

// The contextual help entry point that lives in the top bar. It resolves
// the current route to the most specific help article and opens it in a
// slide-over, so help is always one click away and already scoped to the
// page the user is on.
export function HelpButton() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const role = (getRole() ?? "viewer") as Role;
  const article = articleForPath(location.pathname, role);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Close on route change so the drawer never lingers on the wrong page.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    // Move focus into the dialog so keyboard/screen-reader users start inside it.
    panelRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-riot-navy-500 hover:bg-riot-navy-50 hover:text-riot-navy-700 dark:text-riot-navy-300 dark:hover:bg-riot-navy-800"
        aria-label="Help for this page"
        aria-haspopup="dialog"
      >
        <HelpCircle size={15} />
        <span className="hidden sm:inline">Help</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-labelledby="help-slideover-title">
          <div className="absolute inset-0 bg-black/40" aria-hidden="true" onClick={close} />
          <div
            ref={panelRef}
            tabIndex={-1}
            className="relative flex h-full w-full max-w-md flex-col border-l border-riot-navy-100 bg-riot-navy-50 shadow-xl outline-none dark:border-riot-navy-700 dark:bg-riot-navy-950"
          >
            <div className="flex items-center justify-between border-b border-riot-navy-100 bg-white px-4 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900">
              <div className="flex items-center gap-2">
                {article ? <article.icon size={18} className="text-riot-gold-500" /> : <HelpCircle size={18} className="text-riot-gold-500" />}
                <span id="help-slideover-title" className="text-sm font-semibold text-riot-navy-900 dark:text-white">
                  {article ? article.title : "Help"}
                </span>
              </div>
              <button
                onClick={close}
                className="rounded p-1 text-riot-navy-400 hover:bg-riot-navy-50 hover:text-riot-navy-700 dark:hover:bg-riot-navy-800 dark:hover:text-white"
                aria-label="Close help"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {article ? (
                <>
                  <p className="mb-3 text-sm text-riot-navy-500 dark:text-riot-navy-400">{article.summary}</p>
                  <HelpArticleView article={article} currentRole={role} onNavigate={close} />
                </>
              ) : (
                <p className="text-sm text-riot-navy-500 dark:text-riot-navy-400">
                  No guide is linked to this page yet. Open the Help Center to browse all guides.
                </p>
              )}
            </div>

            <div className="border-t border-riot-navy-100 bg-white px-4 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900">
              <Link
                to={article ? `/help/${article.id}` : "/help"}
                onClick={close}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-riot-gold-600 hover:text-riot-gold-700 dark:text-riot-gold-400"
              >
                Open full Help Center →
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
